import { useMemo, useRef, useState } from 'react';
import { DataError, useList } from '@hana/data';
import { findCarrier, findPort, rateValidity } from '@hana/domain';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Drawer, Input, Segmented, Select, StateView, useToast, type ScreenState } from '@hana/ui';
import { IconBell, IconBuildingWarehouse, IconClock, IconDots, IconExternalLink, IconFilter, IconListCheck, IconLock, IconMessageQuestion, IconPlus, IconRoute, IconSearch } from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useT } from '@hana/i18n';
import { useP2PAccess } from './access';
import { COST_CARRIERS, COST_SERVE } from './costFixtures';
import { CONT_KEYS, filterCost, lookupCost, routeLabel, type ContKey, type LookupRow } from './costRates';
import { DeskBoard } from './DeskBoard';
import { DESK_REGIONS, type DeskDock, type RegionKey } from './desk';
import { DESK_INQUIRIES, DESK_JOB12, DESK_RATES, DESK_SERVE, DESK_TASKS } from './deskFixtures';
import { BOARD_TODAY } from './fixtures';
import { MoreTabs } from './MoreTabs';
import { useWide } from './useWide';
import { dm, usd } from './format';
import type { CostBuy, CostRate } from './types';
import './RatesScreen.i18n';
import s from './rates.module.css';

// TODO: "hôm nay" thật khi đổi sang dữ liệu thật; dữ liệu mẫu neo ngày 23/Oct như màn P2P.
const TODAY = BOARD_TODAY;
type T = (key: string, vars?: Record<string, string | number>) => string;
const ROW_H = 52;
const COLS = '176px 120px repeat(4, 84px) 116px 112px 124px 96px';
const MASK = '•••';
// TODO(A): danh sách 24 hãng dài hơn 7 lựa chọn — đổi sang combobox khi kit có.
const carrierOpts = (t: T) => [{ value: '', label: t('rt.allCarriers') }, ...COST_CARRIERS.map((c) => ({ value: c, label: `${c} · ${findCarrier(c)?.name ?? c}` }))];

type Tab = 'desk' | 'rates' | 'lookup' | 'an' | 'sur' | 'sync';

/** Bảng giá ~18.000 dòng: chỉ dựng các dòng đang thấy (TanStack Virtual). Giá mua chỉ có khi `buy` khác null (quyền `money`). */
function RatesTable({ rows, buy }: { rows: readonly CostRate[]; buy: ReadonlyMap<string, CostBuy> | null }) {
  const t = useT();
  const scroller = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({ count: rows.length, getScrollElement: () => scroller.current, estimateSize: () => ROW_H, overscan: 12 });
  const heads = [t('rt.hLane'), t('rt.hCarrier'), '20DC', '40DC', '40HC', '40RF', t('rt.hValid'), t('rt.hFree'), t('rt.hSrc'), t('rt.hQuote')];
  return (
    <section className={s.card} aria-label={t('rt.tableAria')}>
      <div className={s.vHead} style={{ gridTemplateColumns: COLS }} role="row">
        {heads.map((h, i) => (
          <span key={h} role="columnheader" className={i >= 2 && i <= 5 ? s.num : undefined}>
            {h}
          </span>
        ))}
      </div>
      <div ref={scroller} className={s.vBody} role="table" aria-label={t('rt.tableAria')} aria-rowcount={rows.length}>
        <div style={{ height: v.getTotalSize(), position: 'relative' }}>
          {v.getVirtualItems().map((it) => {
            const r = rows[it.index] as CostRate;
            const val = rateValidity({ validFrom: r.validFrom, validEnd: r.validEnd }, TODAY);
            const b = buy?.get(r.id);
            const px = [b?.dc20, b?.dc40, b?.hc40, b?.rf40];
            const tone = val.status === 'expired' ? s.vExp : val.status === 'expiring' ? s.vSoon : undefined;
            return (
              <div key={r.id} role="row" aria-rowindex={it.index + 1} className={`${s.vRow} ${val.status === 'expired' ? s.dimRow : ''}`} style={{ gridTemplateColumns: COLS, height: ROW_H, transform: `translateY(${it.start}px)` }}>
                <span role="cell" className={s.two}>
                  <b>{routeLabel(r)}</b>
                  <small>
                    {r.via ?? 'Direct'} · {t('rt.dayN', { n: r.transitDays })}
                  </small>
                </span>
                <span role="cell" title={findCarrier(r.carrier)?.name ?? r.carrier}>
                  {r.carrier}
                </span>
                {px.map((p, i) => (
                  <span key={i} role="cell" className={s.num} aria-label={b ? undefined : t('rt.maskAria')}>
                    {b ? (p ? usd(p) : '–') : MASK}
                  </span>
                ))}
                <span role="cell" className={s.two}>
                  <b className={tone}>{dm(r.validEnd)}/2026</b>
                  <small className={tone}>{val.status === 'expired' ? t('rt.expiredAgo', { n: -val.daysLeft }) : t('rt.leftN', { n: val.daysLeft })}</small>
                </span>
                <span role="cell">
                  {t('rt.demdet', { a: r.dem, b: r.det })}
                </span>
                <span role="cell">{r.source}</span>
                <span role="cell" className={s.num} title={t('rt.useTitle', { q: r.quotes, j: r.jobs })}>
                  {r.quotes} · {r.jobs}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className={s.foot}>{t('rt.foot', { n: rows.length.toLocaleString('vi-VN') })}</div>
    </section>
  );
}

function RatesWithBuy({ rows }: { rows: readonly CostRate[] }) {
  const buys = useList('costBuy');
  const map = useMemo(() => new Map((buys.data?.items ?? []).map((b) => [b.id, b])), [buys.data]);
  return <RatesTable rows={rows} buy={map} />;
}

const POD_OPTS = [...COST_SERVE.keys()].map((k) => ({ value: k.split('→')[1] ?? '', label: `${k.split('→')[1]} · ${findPort(k.split('→')[1])?.name ?? ''}` }));
const POL = 'VNSGN';

/** Điện thoại — "Tra cứu nhanh" (Mint-Rates-Phone): chọn cảng đến + loại cont → mỗi hãng một thẻ, bấm ra chi tiết dòng giá. */
function CostLookup({ rates, buys }: { rates: readonly CostRate[]; buys: ReadonlyMap<string, CostBuy> | null }) {
  const t = useT();
  const [pod, setPod] = useState(POD_OPTS[0]?.value ?? '');
  const [cont, setCont] = useState<ContKey>('hc40');
  const [sel, setSel] = useState<LookupRow | null>(null);
  const [podOpen, setPodOpen] = useState(false);
  const podLabel = POD_OPTS.find((o) => o.value === pod)?.label ?? pod;
  const res = useMemo(() => lookupCost(rates, COST_SERVE, { pol: POL, pod, cont, today: TODAY }, buys), [rates, buys, pod, cont]);
  const sb = sel ? buys?.get(sel.rate.id) : undefined;
  return (
    <>
      <section className={s.lkBox} aria-label={t('rt.quick')}>
        <div className={s.lkPair}>
          <div className={s.lkPol}>
            <span>
              <IconRoute size={14} aria-hidden="true" /> {t('rt.pol')}
            </span>
            <b>{findPort(POL)?.name ?? POL} ({POL})</b>
          </div>
          <button type="button" className={s.lkPod} aria-haspopup="dialog" onClick={() => setPodOpen(true)}>
            <span>
              <IconRoute size={16} aria-hidden="true" /> {t('rt.pod')}
            </span>
            <b>{podLabel} ▾</b>
          </button>
        </div>
        <div className={s.lkSeg} role="group" aria-label={t('rt.contAria')}>
          {CONT_KEYS.map((c) => (
            <button key={c.key} type="button" className={s.lkSegBtn} aria-pressed={cont === c.key} onClick={() => setCont(c.key)}>
              {c.label}
            </button>
          ))}
        </div>
      </section>
      <Drawer open={podOpen} onOpenChange={setPodOpen} title={t('rt.podSheet')}>
        <div className={s.chips} role="group" aria-label={t('rt.pod')}>
          {POD_OPTS.map((o) => (
            <button key={o.value} type="button" className={s.chipBtn} aria-pressed={pod === o.value} onClick={() => { setPod(o.value); setPodOpen(false); }}>
              {o.label}
            </button>
          ))}
        </div>
      </Drawer>
      {!buys && (
        <div className={s.note} role="note">
          <IconLock size={16} aria-hidden="true" />
          {t('rt.buyHidden')}
        </div>
      )}
      <div className={s.lkCount}>
        <b>{t('rt.hasRate', { n: res.rows.length })}</b>
        <span>{buys ? t('rt.cheapUsd') : t('rt.byTT')}</span>
      </div>
      {res.rows.map((r) => (
        <button key={r.carrier} type="button" className={s.lkCard} onClick={() => setSel(r)}>
          <span className={s.lkTop}>
            <span className={s.lkCar}>
              <b>{r.carrier}</b>
              <span className={r.expiring ? s.vSoon : s.lkSub} title={t('rt.validTitle', { d: dm(r.rate.validEnd) })}>
                {t('rt.dayN', { n: r.daysLeft })}
              </span>
            </span>
            <span className={s.lkBuyBox}>
              <span className={s.lkSub}>{t('rt.buyLbl')}</span>
              <span className={s.lkBuy}>{r.buy === null ? MASK : usd(r.buy)}</span>
            </span>
          </span>
          <span className={s.lkMeta}>
            <span title={t('rt.route', { v: r.rate.via ?? 'Direct' })}>
              <IconRoute size={14} aria-hidden="true" /> {r.rate.via ? t('rt.viaP', { v: r.rate.via }) : t('rt.direct')}
            </span>
            <span title={t('rt.tt', { n: r.rate.transitDays })}>
              <IconClock size={14} aria-hidden="true" /> {t('rt.dayN', { n: r.rate.transitDays })}
            </span>
            {r.trend !== null && <span title={t('rt.vsPrev')}>{r.trend > 0 ? '▲' : r.trend < 0 ? '▼' : '–'}</span>}
          </span>
        </button>
      ))}
      {res.none.map((c) => (
        <div key={c} className={s.lkNone}>
          <div>
            <b>{c}</b>
            <div>{t('rt.noLane')}</div>
          </div>
          <Button variant="secondary" disabled title={t('rt.askTitle')}>
            {t('rt.askBtn')}
          </Button>
        </div>
      ))}
      <Drawer open={!!sel} onOpenChange={(o) => !o && setSel(null)} title={sel ? `${POL} → ${pod} · ${sel.carrier}` : ''} description={sel ? `${findCarrier(sel.carrier)?.name ?? sel.carrier} · ${sel.rate.via ?? 'Direct'} · ${t('rt.dayN', { n: sel.rate.transitDays })}` : undefined}>
        {sel && (
          <div className={s.dt}>
            <h3>{t('rt.buyUsd')}</h3>
            <div className={s.dtPrice}>
              {CONT_KEYS.map((c) => (
                <div key={c.key}>
                  <span>{c.label}</span>
                  <b>{sb ? usd(sb[c.key]) : MASK}</b>
                </div>
              ))}
            </div>
            <h3>{t('rt.secValid')}</h3>
            <dl>
              <dt>{t('rt.until')}</dt>
              <dd>{dm(sel.rate.validEnd)}/2026 · {t('rt.leftN', { n: sel.daysLeft })}</dd>
            </dl>
            <h3>{t('rt.secRoute')}</h3>
            <dl>
              <dt>{t('rt.via')}</dt>
              <dd>{sel.rate.via ?? 'Direct'}</dd>
              <dt>{t('rt.hFree')}</dt>
              <dd>{t('rt.demdet', { a: sel.rate.dem, b: sel.rate.det })}</dd>
              <dt>{t('rt.hSrc')}</dt>
              <dd>{sel.rate.source}</dd>
            </dl>
            <h3>{t('rt.secUse')}</h3>
            <dl>
              <dt>{t('rt.liveQuote')}</dt>
              <dd>{sel.rate.quotes}</dd>
              <dt>{t('rt.jobsUse')}</dt>
              <dd>{sel.rate.jobs}</dd>
            </dl>
          </div>
        )}
      </Drawer>
    </>
  );
}

function LookupWithBuy({ rates }: { rates: readonly CostRate[] }) {
  const buys = useList('costBuy');
  const map = useMemo(() => new Map((buys.data?.items ?? []).map((b) => [b.id, b])), [buys.data]);
  return <CostLookup rates={rates} buys={map} />;
}

export function RatesScreen() {
  const t = useT();
  const access = useP2PAccess();
  const layout = useLayout();
  const phone = layout === 'phone';
  const pad = layout === 'pad';
  const [region, setRegion] = useState<RegionKey>('all');
  const [mainCar, setMainCar] = useState(false);
  const rates = useList('costRate');
  const [tabRaw, setTab] = useState<Tab>('desk');
  const tab: Tab = phone ? (tabRaw === 'lookup' ? 'lookup' : 'desk') : tabRaw === 'lookup' ? 'rates' : tabRaw;
  const [q, setQ] = useState('');
  const [carrier, setCarrier] = useState<string | null>(null);
  const [hideExpired, setHideExpired] = useState(true);

  const all = (rates.data?.items ?? []) as CostRate[];
  const rows = useMemo(() => filterCost(all, { q, group: null, carrier, hideExpired }, TODAY), [all, q, carrier, hideExpired]);

  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (rates.isPending) state = 'loading';
  else if (rates.error) state = rates.error instanceof DataError && rates.error.code === 'noperm' ? 'noperm' : 'error';
  else if (!all.length) state = 'empty';

  const toast = useToast();
  const wide = useWide();
  const [dock, setDock] = useState<DeskDock | null>('q');
  const open = (route: string, c: string | null) => {
    setQ(route.split(' → ')[1] ?? '');
    setCarrier(c);
    setHideExpired(false);
    setTab(phone ? 'lookup' : 'rates');
  };
  const retry = () => void rates.refetch();

  const body =
    tab === 'desk' ? (
      (
        <DeskBoard wide={wide} pad={pad} phone={phone} region={region} main={mainCar} dock={dock} onDock={setDock} rates={DESK_RATES} serve={DESK_SERVE} inquiries={DESK_INQUIRIES} tasks={DESK_TASKS} job12={DESK_JOB12} money={access.money} onOpen={open} />
      )
    ) : tab === 'an' || tab === 'sur' || tab === 'sync' ? (
      <StateView state="empty" title={tab === 'an' ? t('rt.tAn') : tab === 'sur' ? t('rt.tSur') : t('rt.tSync')} desc={t('rt.building')} />
    ) : tab === 'lookup' ? (
      access.money ? <LookupWithBuy rates={all} /> : <CostLookup rates={all} buys={null} />
    ) : (
      <>
        <div className={s.bar}>
          <div className={s.search}>
            <Input aria-label={t('rt.searchPh')} placeholder={t('rt.searchPh')} icon={<IconSearch size={16} aria-hidden="true" />} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className={s.sel}>
            <Select aria-label={t('rt.hCarrier')} value={carrier ?? ''} onChange={(e) => setCarrier(e.target.value || null)} options={carrierOpts(t)} />
          </div>
          <button type="button" className={s.chipBtn} aria-pressed={!hideExpired} onClick={() => setHideExpired(!hideExpired)} title={t('rt.showExpired')}>
            {t('rt.expiredChip')}
          </button>
          {(q || carrier) && (
            <Button variant="ghost" onClick={() => { setQ(''); setCarrier(null); }}>
              {t('rt.clear')}
            </Button>
          )}
        </div>
        {!access.money && (
          <div className={s.note} role="note">
            <IconLock size={16} aria-hidden="true" />
            {t('rt.masked')}
          </div>
        )}
        {rows.length === 0 ? (
          <StateView state="empty" title={t('rt.noMatch')} desc={t('rt.noMatchD')} />
        ) : access.money ? (
          <RatesWithBuy rows={rows} />
        ) : (
          <RatesTable rows={rows} buy={null} />
        )}
      </>
    );

  return (
    <>
      <Page kind="table">
      {phone ? (
        <PageHeader
          title={tab === 'lookup' ? t('rt.lookupTitle') : t('dk.queue')}
          actions={
            <>
              <HeaderTool label={t('rt.openP2P')} icon={IconExternalLink} onClick={() => window.location.assign('/ezy')} />
              <HeaderTool label={t('rt.filter')} icon={IconFilter} />
            </>
          }
        />
      ) : pad ? (
        <PageHeader
          title="Freight Cost"
          actions={
            <>
              <a className={s.padLink} href="/ezy"><IconExternalLink size={16} aria-hidden="true" />{t('rt.openP2P')}</a>
              <a className={s.padLink} href="/ezy/inquiry"><IconMessageQuestion size={16} aria-hidden="true" />{t('rt.inqPartner')}<span className={s.padN}>{DESK_INQUIRIES.length}</span></a>
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('rt.noWrite'), tone: 'err' })}>{t('rt.addRate')}</Button>
            </>
          }
        />
      ) : (
        <PageHeader
          title="Freight Cost"
          tabs={
            <MoreTabs
              label={t('rt.section')}
              value={tab}
              onChange={setTab}
              main={[{ key: 'desk', label: t('rt.tDesk'), n: DESK_TASKS.length }, { key: 'rates', label: t('rt.tList') }, { key: 'an', label: t('rt.tAn') }]}
              more={[{ key: 'sur', label: t('rt.tSur') }, ...(access.money ? [{ key: 'sync' as const, label: t('rt.tSync') }] : [])]}
            />
          }
          actions={
            <>
              {wide && <HeaderTool label={t('rt.qTool')} icon={IconListCheck} active={dock === 'q'} onClick={() => setDock(dock === 'q' ? null : 'q')} />}
              <HeaderTool label={t('rt.noti')} icon={IconBell} n={5} active={tab === 'desk' && dock === 'noti'} onClick={() => setDock(dock === 'noti' ? 'q' : 'noti')} />
              <HeaderTool label={t('rt.more')} icon={IconDots} n={DESK_INQUIRIES.length} />
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('rt.noWrite'), tone: 'err' })}>{t('rt.addRate')}</Button>
            </>
          }
        />
      )}
      <div className={s.page}>
        {pad && (
          <div className={s.padBar}>
            <MoreTabs pad label={t('rt.section')} value={tab} onChange={setTab} main={[{ key: 'desk', label: t('rt.tDesk'), n: DESK_TASKS.length }, { key: 'rates', label: t('rt.tList') }]} more={[]} />
            {tab === 'desk' && (
              <>
                <span className={s.grow} />
                <div className={s.padSeg} role="group" aria-label={t('dk.group')}>
                  {DESK_REGIONS.map((r) => (
                    <button key={r.key} type="button" aria-pressed={region === r.key} onClick={() => setRegion(r.key)}>{t(`dk.r_${r.key}`)}</button>
                  ))}
                </div>
                <button type="button" className={s.padMain} aria-pressed={mainCar} title={t('dk.mainTip')} onClick={() => setMainCar(!mainCar)}>{t('dk.main')}</button>
              </>
            )}
          </div>
        )}
        {phone && <Segmented label={t('rt.section')} value={tab} onChange={setTab} items={[{ key: 'desk', label: t('rt.tQueue') }, { key: 'lookup', label: t('rt.tLookup') }]} />}
        <StateView
          state={state === 'empty' ? 'empty' : state}
          icon={state === 'empty' ? <IconBuildingWarehouse size={26} stroke={1.8} /> : undefined}
          title={state === 'empty' ? t('rt.emptyT') : state === 'noperm' ? t('rt.nopermT') : state === 'error' ? t('rt.errT') : undefined}
          desc={state === 'noperm' ? t('rt.nopermD') : state === 'error' ? t('rt.errD') : undefined}
          action={state === 'error' ? <Button onClick={retry}>{t('rt.retry')}</Button> : undefined}
        >
          {body}
        </StateView>
      </div>
      </Page>
    </>
  );
}
